<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/BroadcastDetail.vue -->
<script setup>
import { ref, computed, onMounted, defineEmits } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import ToastFix from 'dashboard/components-next/toast/ToastFix.vue';
import RetryBroadcastModal from './modals/RetryBroadcastModal.vue';

// Define emitted events
const emit = defineEmits(['back', 'close']);

const route = useRoute();
const router = useRouter();
const store = useStore();
// useAlert is called directly, not assigned to a variable

// State
const broadcast = ref(null);
const isLoading = ref(true);
const error = ref(null);
const showRetryModal = ref(false);

// Computed property to check if there's a connected inbox
const hasConnectedInbox = computed(() => {
  const allInboxes = store.getters['inboxMetadata/getAllInboxesMetadata'];
  // If getAllInboxesMetadata returns an empty object {}, there are no connected inboxes
  return Object.keys(allInboxes).length > 0;
});

// Get authentication headers from cookie
const getAuthHeaders = () => {
  const getCookie = name => {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? decodeURIComponent(match[2]) : null;
  };

  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

// Computed properties
const broadcastId = computed(() => route.params.id);

const successfulPhones = computed(() => {
  if (!broadcast.value || !broadcast.value.successful_phones) return [];

  try {
    const data = JSON.parse(broadcast.value.successful_phones);
    const successfulList = Array.isArray(data) ? data : [];

    // Match with totalRecipients to get names
    return successfulList.map(item => {
      const recipient = totalRecipients.value.find(r => r.phone === item.phone);
      return {
        ...item,
        name: recipient ? recipient.name : item.name || null,
      };
    });
  } catch (e) {
    console.error('Error parsing successful phones:', e);
    return [];
  }
});

const failedPhones = computed(() => {
  if (!broadcast.value || !broadcast.value.failed_phones) return [];

  try {
    const data = JSON.parse(broadcast.value.failed_phones);
    const failedList = Array.isArray(data) ? data : [];

    // Match with totalRecipients to get names
    return failedList.map(item => {
      const recipient = totalRecipients.value.find(r => r.phone === item.phone);
      return {
        ...item,
        name: recipient ? recipient.name : item.name || null,
      };
    });
  } catch (e) {
    console.error('Error parsing failed phones:', e);
    return [];
  }
});

const totalRecipients = computed(() => {
  if (!broadcast.value || !broadcast.value.total_recipients) return [];

  try {
    const data = JSON.parse(broadcast.value.total_recipients);
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('Error parsing total recipients:', e);
    return [];
  }
});

const pendingPhones = computed(() => {
  if (!broadcast.value || !broadcast.value.total_recipients) return [];

  const successful = successfulPhones.value.map(p => p.phone);
  const failed = failedPhones.value.map(p => p.phone);

  // Filter total recipients to get only pending ones
  return totalRecipients.value.filter(
    recipient =>
      !successful.includes(recipient.phone) && !failed.includes(recipient.phone)
  );
});

// Methods
const fetchBroadcast = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await axios.get(
      `/api/v1/bulk_messages/${broadcastId.value}`,
      {
        headers: getAuthHeaders(),
      }
    );

    broadcast.value = response.data;
  } catch (err) {
    console.error('Error fetching broadcast details:', err);
    error.value =
      err.response?.data?.error || 'Erro ao carregar detalhes do disparo';
  } finally {
    isLoading.value = false;
  }
};

const navigateBack = () => {
  // Always emit the back event first for parent components that might be listening
  emit('back');

  // Navigate back to the bulk message history page
  router.push({ name: 'bulkMessage_history' });
};

const cancelBroadcast = async () => {
  try {
    await axios.post(
      `/api/v1/bulk_messages/${broadcastId.value}/cancel`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );

    useAlert('Disparo cancelado com sucesso', 'success');

    // Refresh data
    fetchBroadcast();
  } catch (error) {
    console.error('Error cancelling broadcast:', error);
    useAlert('Erro ao cancelar disparo', 'error');
  }
};

const retryBroadcast = () => {
  console.log('Current broadcast data for retry:');
  console.log(
    'Total recipients:',
    totalRecipients.value.map(r => ({
      name: r.name,
      phone: r.phone,
      inbox_id: r.inbox_id,
    }))
  );
  console.log(
    'Pending phones:',
    pendingPhones.value.map(r => ({
      name: r.name,
      phone: r.phone,
      inbox_id: r.inbox_id,
    }))
  );

  // Open the retry modal instead of calling API directly
  showRetryModal.value = true;
};

const handleRetryProceed = data => {
  // Close the modal
  showRetryModal.value = false;

  console.log(
    'Data received from RetryBroadcastModal:',
    data.recipients.map(r => ({
      name: r.name,
      phone: r.phone,
      inbox_id: r.inbox_id,
    }))
  );

  // Navigate to BulkMessageNew with the selected recipients
  const queryParams = {
    recipients: JSON.stringify(data.recipients),
    message: data.broadcast.message,
    broadcastId: data.broadcast.id,
  };

  console.log('Query params being sent:', queryParams);

  // Add template info if present
  if (data.broadcast.template_name) {
    queryParams.templateName = data.broadcast.template_name;
    queryParams.templateId = data.broadcast.template_id;
  }

  router.push({
    name: 'bulkMessage_new',
    query: queryParams,
  });
};

// Helper functions
const formatDate = dateString => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatTime = dateString => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 'N/A';

  const start = new Date(startDate);
  const end = new Date(endDate);
  const durationMs = end - start;

  // Convert to seconds
  let seconds = Math.floor(durationMs / 1000);

  // Less than a minute
  if (seconds < 60) {
    return `${seconds} segundos`;
  }

  // Format as minutes and seconds
  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  // Less than an hour
  if (minutes < 60) {
    return `${minutes}m ${seconds}s`;
  }

  // Format as hours, minutes, seconds
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${hours}h ${remainingMinutes}m ${seconds}s`;
};

// Use store getter for integration type formatting
const getInboxTypeFormatted = type => {
  return store.getters['inboxMetadata/formatIntegrationType'](type);
};

const formatStatus = status => {
  if (!status) return 'Desconhecido';

  const statuses = {
    pending: 'Pendente',
    completed: 'Concluído',
    failed: 'Falhou',
    cancelled: 'Cancelado',
  };

  return statuses[status] || status;
};

const getStatusClass = broadcast => {
  const status = broadcast.status;

  if (!status) return 'bg-n-slate-1 text-n-slate-9';

  const classes = {
    pending:
      'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-11',
    completed:
      'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    failed: 'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-11',
    cancelled:
      'bg-n-ruby-2 text-n-ruby-11 dark:bg-n-ruby-3 dark:text-n-ruby-11',
  };

  return (
    classes[status] ||
    'bg-n-slate-1 text-n-slate-9 dark:bg-n-slate-3 dark:text-n-slate-11'
  );
};

// Helper methods to extract counts
const getSuccessCount = broadcast => {
  return successfulPhones.value.length;
};

const getFailureCount = broadcast => {
  return failedPhones.value.length;
};

const getPendingCount = broadcast => {
  return (
    totalRecipients.value.length -
    (successfulPhones.value.length + failedPhones.value.length)
  );
};

const getCompletionPercentage = broadcast => {
  const total = totalRecipients.value.length;
  if (total === 0) return 0;

  const processed = successfulPhones.value.length + failedPhones.value.length;
  return Math.round((processed / total) * 100);
};

// Lifecycle hooks
onMounted(() => {
  if (broadcastId.value) {
    fetchBroadcast();
  } else {
    error.value = 'ID do disparo não encontrado';
    isLoading.value = false;
  }
});

const getProgressBarColor = broadcast => {
  const status = broadcast.status;

  if (!status) return '#9ca3af'; // Default gray

  const colors = {
    pending: '#10b981',
    completed: '#10b981',
    failed: '#10b981',
    cancelled: '#10b981',
  };

  return colors[status] || '#9ca3af';
};
</script>

<template>
  <div class="broadcast-detail">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <div
        class="w-12 h-12 rounded-full border-4 border-n-slate-3 dark:border-n-slate-6 border-t-n-brand dark:border-t-primary-500 animate-spin"
      >
        >
      </div>
      <p class="ml-4 text-n-slate-11 dark:text-n-slate-11">
        Carregando detalhes do disparo...
      </p>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error && !isLoading"
      class="p-6 bg-n-ruby-1 dark:bg-red-900/20 border border-n-ruby-3 dark:border-red-800 rounded-lg text-center my-8"
    >
      <i
        class="i-lucide-alert-triangle text-4xl text-n-ruby-9 dark:text-red-400 mb-2"
      />
      <h3 class="text-lg font-medium text-n-ruby-11 dark:text-red-400 mb-2">
        Erro ao carregar detalhes
      </h3>
      <p class="text-n-ruby-10 dark:text-red-400 mb-4">{{ error }}</p>
      <div class="flex justify-center">
        <Button
          label="Voltar"
          icon="i-lucide-arrow-left"
          color="slate"
          variant="outline"
          @click="navigateBack"
        />
        <Button
          label="Tentar novamente"
          icon="i-lucide-refresh-cw"
          color="blue"
          variant="outline"
          class="ml-2"
          @click="fetchBroadcast"
        />
      </div>
    </div>

    <!-- Broadcast details -->
    <div v-else-if="broadcast" class="w-full mx-auto p-4 sm:p-6 bg-n-slate-1">
      <div class="space-y-6">
        <!-- Header with back button and actions -->
        <div
          class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4"
        >
          <div class="flex items-center">
            <Button
              icon="i-lucide-arrow-left"
              variant="outline"
              color="slate"
              size="sm"
              @click="navigateBack"
            />
            <h2
              class="text-lg sm:text-xl font-medium text-n-slate-12 dark:text-n-slate-12 ml-3 sm:ml-4"
            >
              Detalhes do Disparo
              <span class="text-n-slate-9 dark:text-n-slate-11 font-normal"
                >#{{ broadcast.id }}</span
              >
            </h2>
          </div>

          <div class="flex gap-2">
            <Button
              label="Reenviar"
              icon="i-lucide-repeat"
              variant="outline"
              color="amber"
              size="sm"
              :disabled="!hasConnectedInbox"
              :title="!hasConnectedInbox ? 'Conecte uma caixa de entrada do WhatsApp para reenviar mensagens' : 'Reenviar disparo'"
              :class="!hasConnectedInbox ? '!cursor-not-allowed' : ''"
              @click="retryBroadcast"
            />
          </div>
        </div>

        <!-- Componente de correção de toast -->
        <ToastFix />

        <!-- Status card -->
        <div
          class="bg-white dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg overflow-hidden shadow-sm"
        >
          <div
            class="px-4 sm:px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
          >
            <h3
              class="text-base sm:text-lg font-medium text-n-slate-12 dark:text-n-slate-12"
            >
              Status do Disparo
            </h3>
            <span
              class="px-3 py-1 rounded-full text-xs sm:text-sm font-medium inline-block"
              :class="getStatusClass(broadcast)"
            >
              {{ formatStatus(broadcast.status) }}
            </span>
          </div>

          <div
            class="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6"
          >
            <!-- Integration type -->
            <div class="flex flex-col">
              <span
                class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11 mb-1"
                >Tipo de Integração</span
              >
              <div class="flex items-center">
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center mr-2"
                  :class="getInboxTypeFormatted(broadcast.integration_type).badgeColor"
                >
                  <i
                    :class="getInboxTypeFormatted(broadcast.integration_type).icon"
                  />
                </div>
                <span class="text-n-slate-12 dark:text-n-slate-12">{{
                  getInboxTypeFormatted(broadcast.integration_type).displayName
                }}</span>
              </div>
            </div>

            <!-- Date info -->
            <div class="flex flex-col">
              <span
                class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11 mb-1"
                >Data de criação</span
              >
              <div class="text-n-slate-12 dark:text-n-slate-12">
                {{ formatDate(broadcast.created_at) }} às
                {{ formatTime(broadcast.created_at) }}
              </div>
            </div>

            <!-- Duration -->
            <div v-if="broadcast.completed_at" class="flex flex-col">
              <span
                class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11 mb-1"
                >Duração</span
              >
              <div class="text-n-slate-12 dark:text-n-slate-12">
                {{
                  formatDuration(broadcast.started_at, broadcast.completed_at)
                }}
              </div>
            </div>
          </div>

          <!-- Progress info -->
          <div class="px-4 sm:px-6 pb-4 sm:pb-6">
            <div class="flex items-center justify-between mb-2">
              <span
                class="text-sm font-medium text-n-slate-11 dark:text-n-slate-11"
                >Progresso</span
              >
              <span class="text-sm text-n-slate-12 dark:text-n-slate-12"
                >{{ getCompletionPercentage(broadcast) }}%</span
              >
            </div>

            <!-- Progress bar container with responsive background -->
            <div
              class="w-full rounded-full h-3 overflow-hidden bg-n-slate-3 dark:bg-n-slate-6"
            >
              <!-- Progress bar with inline style for color and longer animation duration -->
              <div
                class="h-3 rounded-full transition-all duration-1000 ease-out"
                :style="{
                  width: `${Math.max(getCompletionPercentage(broadcast), 0.5)}%`,
                  backgroundColor: getProgressBarColor(broadcast),
                }"
              />
            </div>

            <div class="flex justify-between mt-4 text-sm">
              <div class="flex items-center text-green-600 dark:text-green-400">
                <i class="i-lucide-check-circle mr-1" />
                <span
                  >{{ getSuccessCount(broadcast) }} enviados com sucesso</span
                >
              </div>
              <div class="flex items-center text-n-ruby-11 dark:text-n-ruby-11">
                <i class="i-lucide-x-circle mr-1" />
                <span>{{ getFailureCount(broadcast) }} falhas</span>
              </div>
              <div
                class="flex items-center text-n-amber-11 dark:text-n-amber-11"
              >
                <i class="i-lucide-clock mr-1" />
                <span>{{ getPendingCount(broadcast) }} pendentes</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Message content -->
        <div
          class="bg-white dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg overflow-hidden shadow-sm"
        >
          <div
            class="px-4 sm:px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-6"
          >
            <h3
              class="text-base sm:text-lg font-medium text-n-slate-12 dark:text-n-slate-12"
            >
              Conteúdo da Mensagem
            </h3>
          </div>

          <div class="p-4 sm:p-6">
            <!-- Template info if present -->
            <div
              v-if="broadcast.template_name"
              class="mb-4 bg-n-iris-1 dark:bg-n-iris-2 border border-n-iris-3 dark:border-n-iris-6 rounded-lg p-4"
            >
              <div class="flex items-start">
                <i
                  class="i-lucide-template text-n-iris-11 dark:text-n-iris-11 mr-2 mt-1"
                />
                <div>
                  <h4 class="font-medium text-n-iris-11 dark:text-n-iris-11">
                    Template utilizado
                  </h4>
                  <p class="text-sm text-n-iris-10 dark:text-n-iris-11 mt-1">
                    {{ broadcast.template_name }}
                  </p>
                  <div
                    v-if="broadcast.template_id"
                    class="text-xs text-n-iris-9 dark:text-n-iris-11 mt-2"
                  >
                    ID: {{ broadcast.template_id }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Message content -->
            <div
              class="border border-n-slate-3 dark:border-n-slate-6 rounded-lg p-4 bg-n-slate-1 dark:bg-n-slate-2"
            >
              <div
                class="whitespace-pre-wrap text-n-slate-12 dark:text-n-slate-12"
              >
                {{ broadcast.message }}
              </div>
            </div>
          </div>
        </div>

        <!-- Recipients stats -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <!-- Successful phones -->
          <div
            class="bg-white dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg overflow-hidden shadow-sm"
          >
            <div
              class="px-4 sm:px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            >
              <h3
                class="text-base sm:text-lg font-medium text-n-slate-12 dark:text-n-slate-12"
              >
                Enviados com Sucesso
              </h3>
              <div
                class="flex items-center text-green-600 dark:text-green-400"
              >
                <i class="i-lucide-check-circle mr-1" />
                <span>{{ getSuccessCount(broadcast) }}</span>
              </div>
            </div>

            <div class="max-h-60 overflow-y-auto">
              <table
                v-if="successfulPhones.length > 0"
                class="min-w-full divide-y divide-n-slate-3 dark:divide-n-slate-6"
              >
                <thead class="bg-n-slate-1 dark:bg-n-slate-3">
                  <tr>
                    <th
                      scope="col"
                      class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Nome
                    </th>
                    <th
                      scope="col"
                      class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Telefone
                    </th>
                    <th
                      scope="col"
                      class="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Inbox
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="bg-white dark:bg-n-slate-1 divide-y divide-n-slate-3 dark:divide-n-slate-6"
                >
                  <tr v-for="(phone, index) in successfulPhones" :key="index">
                    <td
                      class="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-n-slate-12 dark:text-n-slate-12"
                    >
                      {{ phone.name || '-' }}
                    </td>
                    <td
                      class="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-n-slate-12 dark:text-n-slate-12"
                    >
                      {{ phone.phone }}
                    </td>
                    <td
                      class="hidden sm:table-cell px-6 py-3 whitespace-nowrap text-sm text-n-slate-11 dark:text-n-slate-11"
                    >
                      {{ phone.inbox_id }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                v-else
                class="p-6 text-center text-n-slate-9 dark:text-n-slate-11"
              >
                Nenhum envio concluído com sucesso.
              </div>
            </div>
          </div>

          <!-- Failed phones -->
          <div
            class="bg-white dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg overflow-hidden shadow-sm"
          >
            <div
              class="px-6 py-4 border-b border-n-slate-3 dark:border-slate-600 flex items-center justify-between"
            >
              <h3
                class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12"
              >
                Falhas no Envio
              </h3>
              <div class="flex items-center text-n-ruby-11 dark:text-n-ruby-11">
                <i class="i-lucide-x-circle mr-1" />
                <span>{{ getFailureCount(broadcast) }}</span>
              </div>
            </div>

            <div class="max-h-60 overflow-y-auto">
              <table
                v-if="failedPhones.length > 0"
                class="min-w-full divide-y divide-n-slate-3 dark:divide-n-slate-6"
              >
                <thead class="bg-n-slate-1 dark:bg-n-slate-3">
                  <tr>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Nome
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Telefone
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Erro
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="bg-white dark:bg-n-slate-1 divide-y divide-n-slate-3 dark:divide-n-slate-6"
                >
                  <tr v-for="(phone, index) in failedPhones" :key="index">
                    <td
                      class="px-6 py-3 whitespace-nowrap text-sm text-n-slate-12 dark:text-n-slate-12"
                    >
                      {{ phone.name || '-' }}
                    </td>
                    <td
                      class="px-6 py-3 whitespace-nowrap text-sm text-n-slate-12 dark:text-n-slate-12"
                    >
                      {{ phone.phone }}
                    </td>
                    <td
                      class="px-6 py-3 text-sm text-n-ruby-11 dark:text-n-ruby-11 max-w-xs truncate"
                    >
                      {{ phone.error }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                v-else
                class="p-6 text-center text-n-slate-9 dark:text-n-slate-11"
              >
                Nenhuma falha de envio registrada.
              </div>
            </div>
          </div>

          <!-- Pending phones -->
          <div
            class="bg-white dark:bg-n-slate-1 border border-n-slate-3 dark:border-n-slate-6 rounded-lg overflow-hidden shadow-sm"
          >
            <div
              class="px-6 py-4 border-b border-n-slate-3 dark:border-slate-600 flex items-center justify-between"
            >
              <h3
                class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12"
              >
                Pendentes
              </h3>
              <div
                class="flex items-center text-n-amber-11 dark:text-n-amber-11"
              >
                <i class="i-lucide-clock mr-1" />
                <span>{{ getPendingCount(broadcast) }}</span>
              </div>
            </div>

            <div class="max-h-60 overflow-y-auto">
              <table
                v-if="pendingPhones.length > 0"
                class="min-w-full divide-y divide-n-slate-3 dark:divide-n-slate-6"
              >
                <thead class="bg-n-slate-1 dark:bg-n-slate-3">
                  <tr>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Nome
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Telefone
                    </th>
                    <th
                      scope="col"
                      class="px-6 py-3 text-left text-xs font-medium text-n-slate-9 dark:text-n-slate-11 uppercase tracking-wider"
                    >
                      Inbox
                    </th>
                  </tr>
                </thead>
                <tbody
                  class="bg-white dark:bg-n-slate-1 divide-y divide-n-slate-3 dark:divide-n-slate-6"
                >
                  <tr v-for="(recipient, index) in pendingPhones" :key="index">
                    <td
                      class="px-6 py-3 whitespace-nowrap text-sm text-n-slate-12 dark:text-n-slate-12"
                    >
                      {{ recipient.name || '-' }}
                    </td>
                    <td
                      class="px-6 py-3 whitespace-nowrap text-sm text-n-slate-12 dark:text-n-slate-12"
                    >
                      {{ recipient.phone }}
                    </td>
                    <td
                      class="px-6 py-3 whitespace-nowrap text-sm text-n-slate-11 dark:text-slate-400"
                    >
                      {{ recipient.inbox_id }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                v-else
                class="p-6 text-center text-n-slate-9 dark:text-n-slate-11"
              >
                Nenhum contato pendente.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Retry Broadcast Modal -->
    <RetryBroadcastModal
      :show="showRetryModal"
      :broadcast="broadcast"
      @close="showRetryModal = false"
      @proceed="handleRetryProceed"
    />
  </div>
</template>

<style scoped>
.broadcast-detail {
  /* Fill the parent container height */
  height: 100%;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  /* Override any max-height constraint */
  max-height: none !important;
}

/* Make the inner container flexible to fill space */
.broadcast-detail > div {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 2rem; /* Add some bottom padding */
  min-height: 0; /* Allow the flex item to shrink */
}
</style>
